/**
 * GenerateChecklistModal
 *
 * Modal for smart document checklist generation.
 * - Auto-fills deal parameters (lender, loan type, verification level) from deal data
 * - Shows deal parties as checkboxes (all selected by default)
 * - Options: include optional docs, auto-link existing entity docs
 * - Calls POST /api/crm/deals/:dealId/documents/generate-checklist on submit
 *
 * AC001: Modal shows deal parameters (lender, loan type, verification level auto-filled)
 * AC002: Applicants listed with entity type and role badges (all checked by default)
 * AC003: Generate triggers API and success refreshes parent
 * AC004: No-lender warning disables generate button
 * AC005: Options (include optional, auto-link) affect generation payload
 */

import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DealParty {
  id: string;
  partyId: string;
  partyType: "account" | "contact";
  role: string;
  partyName: string;
  accountData?: {
    id: string;
    legalName: string;
    entityType: string;
  } | null;
  contactData?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
}

interface DealPartiesResponse {
  manual: DealParty[];
  facility_propagated: DealParty[];
  association_propagated: DealParty[];
  all: DealParty[];
}

interface Deal {
  id: string;
  dealName: string;
  lenderId: string | null;
  lenderName: string | null;
}

interface GenerateChecklistResponse {
  generated: number;
  autoLinked: number;
  skippedDuplicates?: number;
  breakdown?: unknown[];
}

export interface GenerateChecklistModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  onGenerated: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const LOAN_TYPE_OPTIONS = [
  { value: "home", label: "Home" },
  { value: "business", label: "Business" },
  { value: "commercial", label: "Commercial" },
  { value: "smsf", label: "SMSF" },
];

const VERIFICATION_LEVEL_OPTIONS = [
  { value: "full_doc", label: "Full Doc" },
  { value: "alt_doc", label: "Alt Doc" },
  { value: "low_doc", label: "Low Doc" },
  { value: "no_doc", label: "No Doc" },
];

function getRoleBadgeVariant(role: string): "default" | "secondary" | "outline" | "destructive" {
  switch (role) {
    case "borrower":
      return "default";
    case "guarantor":
      return "secondary";
    default:
      return "outline";
  }
}

function formatRole(role: string): string {
  return role
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function getEntityTypeBadgeLabel(party: DealParty): string {
  if (party.partyType === "account" && party.accountData?.entityType) {
    const t = party.accountData.entityType;
    return t
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }
  if (party.partyType === "contact") {
    return "Individual";
  }
  return "Entity";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GenerateChecklistModal({
  open,
  onOpenChange,
  dealId,
  onGenerated,
}: GenerateChecklistModalProps) {
  const { toast } = useToast();

  // Form state
  const [lenderId, setLenderId] = useState<string>("__none__");
  const [loanType, setLoanType] = useState<string>("__none__");
  const [verificationLevel, setVerificationLevel] = useState<string>("full_doc");
  const [selectedApplicantIds, setSelectedApplicantIds] = useState<string[]>([]);
  const [includeOptional, setIncludeOptional] = useState(false);
  const [autoLinkEntityDocs, setAutoLinkEntityDocs] = useState(true);

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  const { data: deal, isLoading: isDealLoading } = useQuery<Deal>({
    queryKey: ["/api/crm/deals", dealId],
    enabled: open && !!dealId,
  });

  const { data: partiesData, isLoading: isPartiesLoading } = useQuery<DealPartiesResponse>({
    queryKey: ["/api/crm/deals", dealId, "parties"],
    enabled: open && !!dealId,
  });

  const allParties: DealParty[] = partiesData?.all ?? [];

  // -------------------------------------------------------------------------
  // Auto-fill from deal data
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!open) {
      // Reset all form state when modal closes
      setLenderId("__none__");
      setLoanType("__none__");
      setVerificationLevel("full_doc");
      setSelectedApplicantIds([]);
      setIncludeOptional(false);
      setAutoLinkEntityDocs(true);
      return;
    }

    if (deal) {
      setLenderId(deal.lenderId ?? "__none__");
    }
  }, [open, deal]);

  // Select all parties by default when parties load
  useEffect(() => {
    if (allParties.length > 0 && open) {
      setSelectedApplicantIds(allParties.map((p) => p.id));
    }
  }, [allParties.length, open]);  

  // -------------------------------------------------------------------------
  // Mutation
  // -------------------------------------------------------------------------

  const generateMutation = useMutation({
    mutationFn: async (config: {
      lenderId?: string;
      loanType?: string;
      verificationLevel?: string;
      applicantIds: string[];
      includeOptional: boolean;
      autoLinkEntityDocs: boolean;
    }) => {
      const response = await apiRequest(
        "POST",
        `/api/crm/deals/${dealId}/documents/generate-checklist`,
        config
      );
      return response.json() as Promise<GenerateChecklistResponse>;
    },
    onSuccess: (data) => {
      const autoLinkedText = data.autoLinked > 0 ? `, auto-linked ${data.autoLinked}` : "";
      toast({
        title: `Generated ${data.generated} documents${autoLinkedText}`,
        description:
          data.skippedDuplicates && data.skippedDuplicates > 0
            ? `${data.skippedDuplicates} duplicate(s) skipped`
            : undefined,
      });
      onGenerated();
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Generation failed",
        description: error.message ?? "An unexpected error occurred",
        variant: "destructive",
      });
    },
  });

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleOpenChange = (newOpen: boolean) => {
    if (!generateMutation.isPending) {
      onOpenChange(newOpen);
    }
  };

  const handleApplicantToggle = (partyId: string, checked: boolean) => {
    setSelectedApplicantIds((prev) =>
      checked ? [...prev, partyId] : prev.filter((id) => id !== partyId)
    );
  };

  const handleGenerate = () => {
    const payload = {
      lenderId: lenderId !== "__none__" ? lenderId : undefined,
      loanType: loanType !== "__none__" ? loanType : undefined,
      verificationLevel: verificationLevel !== "__none__" ? verificationLevel : undefined,
      applicantIds: selectedApplicantIds,
      includeOptional,
      autoLinkEntityDocs,
    };
    generateMutation.mutate(payload);
  };

  // -------------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------------

  const isLoading = isDealLoading || isPartiesLoading;
  const hasNoLender = !deal?.lenderId;
  const canGenerate =
    !generateMutation.isPending && !hasNoLender && selectedApplicantIds.length > 0;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-xl max-h-[90vh] overflow-y-auto"
        data-testid="generate-checklist-modal"
      >
        <DialogHeader>
          <DialogTitle>Generate Document Checklist</DialogTitle>
          <DialogDescription>
            Configure parameters and select applicants to generate a document checklist for this
            deal.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* No-lender warning */}
            {hasNoLender && (
              <Alert variant="destructive" data-testid="no-lender-warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Please assign a lender to this deal before generating a checklist.
                </AlertDescription>
              </Alert>
            )}

            {/* Deal Parameters */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground">
                Deal Parameters
                <span className="ml-1 text-xs font-normal text-muted-foreground">
                  (auto-filled from deal)
                </span>
              </h3>

              <div className="grid gap-3">
                {/* Lender */}
                <div className="grid grid-cols-3 items-center gap-3">
                  <Label className="text-right text-sm">Lender</Label>
                  <div className="col-span-2">
                    {deal?.lenderName ? (
                      <p
                        className="text-sm font-medium"
                        data-testid="lender-name-display"
                      >
                        {deal.lenderName}
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">No lender assigned</p>
                    )}
                  </div>
                </div>

                {/* Loan Type */}
                <div className="grid grid-cols-3 items-center gap-3">
                  <Label htmlFor="loan-type-select" className="text-right text-sm">
                    Loan Type
                  </Label>
                  <div className="col-span-2">
                    <Select
                      value={loanType}
                      onValueChange={setLoanType}
                    >
                      <SelectTrigger
                        id="loan-type-select"
                        data-testid="loan-type-select"
                      >
                        <SelectValue placeholder="Select loan type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Not specified —</SelectItem>
                        {LOAN_TYPE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Verification Level */}
                <div className="grid grid-cols-3 items-center gap-3">
                  <Label htmlFor="verification-level-select" className="text-right text-sm">
                    Verification
                  </Label>
                  <div className="col-span-2">
                    <Select
                      value={verificationLevel}
                      onValueChange={setVerificationLevel}
                    >
                      <SelectTrigger
                        id="verification-level-select"
                        data-testid="verification-level-select"
                      >
                        <SelectValue placeholder="Select verification level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Not specified —</SelectItem>
                        {VERIFICATION_LEVEL_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* Applicants */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">
                Applicants on this deal
              </h3>

              {allParties.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">
                  No parties found on this deal.
                </p>
              ) : (
                <div
                  className="space-y-2 rounded-md border p-3"
                  data-testid="applicants-list"
                >
                  {allParties.map((party) => (
                    <div
                      key={party.id}
                      className="flex items-center gap-3"
                      data-testid={`applicant-row-${party.id}`}
                    >
                      <Checkbox
                        id={`applicant-${party.id}`}
                        checked={selectedApplicantIds.includes(party.id)}
                        onCheckedChange={(checked) =>
                          handleApplicantToggle(party.id, checked === true)
                        }
                        data-testid={`applicant-checkbox-${party.id}`}
                      />
                      <Label
                        htmlFor={`applicant-${party.id}`}
                        className="flex flex-1 cursor-pointer items-center gap-2"
                      >
                        <span className="text-sm font-medium">{party.partyName}</span>
                        <Badge variant="outline" className="text-xs">
                          {getEntityTypeBadgeLabel(party)}
                        </Badge>
                        <Badge
                          variant={getRoleBadgeVariant(party.role)}
                          className="text-xs"
                          data-testid={`party-role-badge-${party.id}`}
                        >
                          {formatRole(party.role)}
                        </Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              )}

              {allParties.length > 0 && selectedApplicantIds.length === 0 && (
                <p className="text-xs text-destructive">
                  Select at least one applicant to generate a checklist.
                </p>
              )}
            </div>

            {/* Options */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">Options</h3>

              <div className="space-y-3 rounded-md border p-3">
                {/* Include optional documents */}
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="include-optional"
                    checked={includeOptional}
                    onCheckedChange={(checked) => setIncludeOptional(checked === true)}
                    data-testid="include-optional-checkbox"
                  />
                  <div className="space-y-0.5">
                    <Label htmlFor="include-optional" className="cursor-pointer text-sm font-medium">
                      Include optional documents
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Add conditional and optional items to the checklist in addition to required
                      documents.
                    </p>
                  </div>
                </div>

                {/* Auto-link existing entity documents */}
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="auto-link-entity-docs"
                    checked={autoLinkEntityDocs}
                    onCheckedChange={(checked) => setAutoLinkEntityDocs(checked === true)}
                    data-testid="auto-link-checkbox"
                  />
                  <div className="space-y-0.5">
                    <Label
                      htmlFor="auto-link-entity-docs"
                      className="cursor-pointer text-sm font-medium"
                    >
                      Auto-link existing entity documents
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Automatically link matching documents from each applicant's entity library.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Generating progress indicator */}
            {generateMutation.isPending && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Analysing requirements...</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={generateMutation.isPending}
            data-testid="button-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={!canGenerate}
            data-testid="button-generate"
          >
            {generateMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {generateMutation.isPending ? "Generating..." : "Generate Checklist"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
